import React from "react";
import { Star, Quote, ArrowRight } from "lucide-react";
import { GOOGLE_REVIEWS_URL } from "../lib/constants";
import { useLang } from "../lib/i18n";

function Stars({ count = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${count} / 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={16}
          className={i < count ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

const INITIALS = ["MG", "JR", "BT", "CM"];

export default function Testimonials() {
  const { t } = useLang();
  const items = t.testimonials.items;
  const avg = "5.0";

  return (
    <section className="section-y" data-testid="testimonials-section">
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="overline">{t.testimonials.overline}</span>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {t.testimonials.title}
            </h2>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                <span className="font-display font-extrabold text-2xl">{avg}</span>
                <Stars count={5} />
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {t.testimonials.basedOn(items.length)}
              </div>
            </div>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline whitespace-nowrap"
            >
              {t.common.readOnGoogle} <ArrowRight size={14} />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((tv, i) => (
            <figure
              key={i}
              data-testid={`testimonial-card-${i}`}
              className="group relative flex flex-col rounded-2xl border border-border bg-card p-6 hover:-translate-y-1 hover:shadow-lg hover:border-primary/40 transition-all duration-300"
            >
              <Quote
                size={28}
                className="text-primary/15 absolute top-5 right-5"
                aria-hidden="true"
                strokeWidth={2.5}
              />
              <Stars count={5} />
              <blockquote className="mt-4 text-sm leading-relaxed text-foreground/90 flex-1">
                "{tv.text}"
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3 pt-5 border-t border-border">
                <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-bold text-sm shrink-0">
                  {INITIALS[i] || tv.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="leading-tight">
                  <div className="font-display font-semibold text-sm">{tv.name}</div>
                  <div className="text-xs text-muted-foreground">{tv.city}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-10 sm:hidden text-center">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-link-mobile"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            {t.common.readMoreOnGoogle} <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
